import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Anchor } from 'lucide-react';
import { Button } from '../components/ui/button';

export const CGV = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            <div className="flex items-center gap-2">
              <Anchor className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-lg">The Myli Boat</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Conditions Générales de Vente</h1>

        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Préambule */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Préambule</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre 
                <strong> The Myli Boat</strong> (ZEMMOUR Salim, Entrepreneur individuel, SIRET : 850 462 664 00036) 
                et toute personne physique ou morale (ci-après « le Client ») souhaitant louer un bateau via le site 
                <a href="https://www.themyliboat.com" className="text-blue-600 hover:underline"> www.themyliboat.com</a>.
              </p>
              <p>
                En réservant une prestation via notre site, le Client reconnaît avoir pris connaissance des présentes CGV 
                et les accepte sans réserve.
              </p>
            </div>
          </section>

          {/* Article 1 - Objet */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 1 - Objet</h2>
            <p className="text-gray-700">
              The Myli Boat propose la location de bateau avec ou sans prestation de service de skipper, 
              au départ du Port de la Pointe Rouge, 13008 Marseille, France.
            </p>
          </section>

          {/* Article 2 - Créneaux et tarifs */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 2 - Créneaux et tarifs</h2>
            <div className="space-y-3 text-gray-700">
              <p>Les créneaux disponibles et leurs tarifs sont les suivants :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Matinée</strong> (09h00 - 13h30) : 350€ (4h30)</li>
                <li><strong>Après-midi</strong> (13h30 - 18h00) : 350€ (4h30)</li>
                <li><strong>Journée complète</strong> (09h00 - 18h00) : 450€ jusqu'au 31 mai / 550€ du 1er juin au 15 septembre (9h)</li>
                <li><strong>Sunset</strong> (18h00 - 22h00) : 250€ (4h)</li>
              </ul>
              <p className="mt-4">
                Ces tarifs sont exprimés en euros et incluent la location du bateau, le carburant, 
                et les équipements de sécurité obligatoires. <strong>TVA non applicable, article 293 B du CGI</strong> (franchise en base de TVA).
              </p>
              <p>
                <strong>Frais additionnels :</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Frais de service : 10€ (obligatoire)</li>
                <li>Assurance météo : 17€ (optionnelle)</li>
              </ul>
            </div>
          </section>

          {/* Article 3 - Réservation et paiement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 3 - Réservation et paiement</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>3.1 Modalités de réservation :</strong><br />
                La réservation s'effectue en ligne via le formulaire de réservation disponible sur notre site. 
                Le Client doit fournir les informations suivantes : nom, prénom, email, téléphone, date et créneau souhaités.
              </p>
              <p>
                <strong>3.2 Paiement de l'acompte :</strong><br />
                Lors de la réservation, un acompte de <strong>30% du montant total</strong> est demandé pour confirmer la réservation. 
                Ce paiement est effectué de manière sécurisée via notre prestataire de paiement <strong>Stripe</strong>.
              </p>
              <p>
                <strong>3.3 Solde :</strong><br />
                Le solde (70% restants) devra être réglé <strong>le jour de la prestation</strong>, au port, 
                en espèces ou par carte bancaire.
              </p>
              <p>
                <strong>3.4 Confirmation :</strong><br />
                Une fois le paiement de l'acompte validé, le Client recevra un email de confirmation avec les détails de sa réservation.
              </p>
            </div>
          </section>

          {/* Article 4 - Annulation et remboursement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 4 - Annulation et remboursement</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>4.1 Annulation par le Client :</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Plus de 7 jours avant la date :</strong> Remboursement intégral de l'acompte (hors frais de service).</li>
                <li><strong>Entre 3 et 7 jours avant la date :</strong> Remboursement de 50% de l'acompte.</li>
                <li><strong>Moins de 3 jours avant la date :</strong> Aucun remboursement.</li>
              </ul>
              <p className="mt-4">
                <strong>4.2 Annulation pour conditions météorologiques défavorables :</strong><br />
                Si les conditions météorologiques ne permettent pas une navigation en toute sécurité 
                (décision prise par le skipper), deux options sont proposées :
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Report de la sortie à une date ultérieure (sous réserve de disponibilité).</li>
                <li>Remboursement intégral de l'acompte.</li>
              </ul>
              <p className="mt-4">
                <strong>4.3 Assurance météo (17€ optionnelle) :</strong><br />
                Si le Client a souscrit à l'assurance météo lors de sa réservation, il bénéficie d'un remboursement 
                intégral de l'acompte en cas d'annulation pour météo défavorable, sans obligation de report.
              </p>
            </div>
          </section>

          {/* Article 5 - Obligations du Client */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 5 - Obligations du Client</h2>
            <div className="space-y-3 text-gray-700">
              <p>Le Client s'engage à :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Se présenter à l'heure convenue au port de départ.</li>
                <li>Respecter les consignes de sécurité données par le skipper.</li>
                <li>Ne pas consommer d'alcool de manière excessive avant ou pendant la sortie.</li>
                <li>Respecter le matériel mis à disposition.</li>
                <li>Ne pas fumer à bord (sauf zones autorisées).</li>
              </ul>
              <p className="mt-4">
                En cas de non-respect de ces règles, le skipper se réserve le droit d'interrompre la sortie 
                sans remboursement.
              </p>
            </div>
          </section>

          {/* Article 6 - Responsabilité et assurances */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 6 - Responsabilité et assurances</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>6.1 Assurance du bateau :</strong><br />
                Le bateau est assuré conformément à la réglementation en vigueur. Cette assurance couvre 
                les dommages matériels et corporels en cas d'accident.
              </p>
              <p>
                <strong>6.2 Responsabilité du Client :</strong><br />
                Le Client est responsable de tout dommage causé au bateau ou aux équipements par négligence 
                ou mauvaise utilisation. Une franchise pourra être appliquée.
              </p>
              <p>
                <strong>6.3 Objets personnels :</strong><br />
                The Myli Boat ne peut être tenu responsable de la perte, du vol ou de la détérioration 
                des objets personnels du Client à bord.
              </p>
            </div>
          </section>

          {/* Article 7 - Droit de rétractation */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 7 - Droit de rétractation</h2>
            <p className="text-gray-700">
              Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation 
              ne s'applique pas aux prestations de services d'hébergement, de transport, de restauration, 
              ou de loisirs qui doivent être fournies à une date ou à une période déterminée.
            </p>
          </section>

          {/* Article 8 - Protection des données */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 8 - Protection des données personnelles</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                Les données personnelles collectées lors de la réservation sont traitées conformément 
                au Règlement Général sur la Protection des Données (RGPD).
              </p>
              <p>
                Pour plus d'informations, consultez notre <a href="/politique-confidentialite" className="text-blue-600 hover:underline font-semibold">Politique de Confidentialité</a>.
              </p>
            </div>
          </section>

          {/* Article 9 - Droit applicable et litiges */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 9 - Droit applicable et litiges</h2>
            <div className="space-y-3 text-gray-700">
              <p>
                Les présentes CGV sont soumises au droit français.
              </p>
              <p>
                En cas de litige, et après recherche d'une solution amiable, les tribunaux compétents 
                seront ceux du ressort du siège social de The Myli Boat (Marseille, France).
              </p>
              <p>
                Conformément à l'article L612-1 du Code de la consommation, le Client peut recourir gratuitement 
                à un médiateur de la consommation en cas de litige : <br />
                <a href="https://www.economie.gouv.fr/mediation-conso" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  https://www.economie.gouv.fr/mediation-conso
                </a>
              </p>
            </div>
          </section>

          {/* Article 10 - Modifications des CGV */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Article 10 - Modifications des CGV</h2>
            <p className="text-gray-700">
              The Myli Boat se réserve le droit de modifier les présentes CGV à tout moment. 
              Les CGV applicables sont celles en vigueur à la date de la réservation.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
            <div className="space-y-2 text-gray-700">
              <p>Pour toute question concernant nos CGV, contactez-nous :</p>
              <p><strong>Email :</strong> <a href="mailto:TheMyliBoat@gmail.com" className="text-blue-600 hover:underline">TheMyliBoat@gmail.com</a></p>
              <p><strong>Téléphone :</strong> <a href="tel:0644731667" className="text-blue-600 hover:underline">06 44 73 16 67</a></p>
            </div>
          </section>

          {/* Date de mise à jour */}
          <section className="pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              <strong>Dernière mise à jour :</strong> {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>

        </div>

        {/* Liens vers autres pages légales */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/mentions-legales')}
            className="w-full py-6"
          >
            ⚖️ Mentions Légales
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/politique-confidentialite')}
            className="w-full py-6"
          >
            🔒 Politique de Confidentialité
          </Button>
        </div>

        {/* Retour accueil */}
        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CGV;
